import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Mail, Phone, MapPin } from "lucide-react";
import { Toaster } from "./ui/toaster";
import { useSiteContent } from "../context/SiteContent";
import SocialLinksRow from "./SocialLinksRow";
import CookieConsent from "./CookieConsent";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

const Header = () => {
  const { personalInfo } = useSiteContent();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  useEffect(() => { setMobileMenuOpen(false); }, [location]);

  const navLinkClass = ({ isActive }) =>
    `text-[13px] tracking-[0.15em] uppercase font-sans transition-colors duration-200 ${isActive ? "text-[#B8945A]" : "text-[#4A4A4A] hover:text-[#1C2B3A]"}`;

  return (
    <header className="fixed top-0 w-full bg-white/95 backdrop-blur-sm z-50 border-b border-[#E5DDD3]">
      <div className="max-w-6xl mx-auto px-6 md:px-12 flex items-center justify-between h-[72px]">
        <Link to="/" className="font-serif text-lg md:text-xl font-bold text-[#1C2B3A] tracking-wide hover:text-[#B8945A] transition-colors duration-200" data-testid="site-logo">{personalInfo.name}</Link>
        <nav className="hidden md:flex items-center gap-8">
          <NavLink to="/" end className={navLinkClass}>Home</NavLink>
          <NavLink to="/about" className={navLinkClass}>About</NavLink>
          <NavLink to="/cv" className={navLinkClass}>Curriculum Vitae</NavLink>
          <NavLink to="/contact" className={navLinkClass}>Contact</NavLink>
        </nav>
        <button className="md:hidden text-[#1C2B3A] p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu">
          {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      <div className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${mobileMenuOpen ? "max-h-64 opacity-100" : "max-h-0 opacity-0"}`}>
        <nav className="flex flex-col py-4 px-6 space-y-4 bg-white border-b border-[#E5DDD3]">
          <NavLink to="/" end className={navLinkClass}>Home</NavLink>
          <NavLink to="/about" className={navLinkClass}>About</NavLink>
          <NavLink to="/cv" className={navLinkClass}>Curriculum Vitae</NavLink>
          <NavLink to="/contact" className={navLinkClass}>Contact</NavLink>
        </nav>
      </div>
    </header>
  );
};

const Footer = () => {
  const { personalInfo, socialLinks } = useSiteContent();
  return (
    <footer className="bg-[#1C2B3A] text-white">
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          <div>
            <h3 className="font-serif text-lg font-bold mb-4">{personalInfo.name}</h3>
            <p className="text-sm text-white/60 leading-relaxed">{personalInfo.tagline}</p>
          </div>
          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] text-[#B8945A] mb-5 font-sans font-bold">Quick Links</h4>
            <nav className="flex flex-col space-y-3">
              <Link to="/" className="text-sm text-white/60 hover:text-[#B8945A] transition-colors">Home</Link>
              <Link to="/about" className="text-sm text-white/60 hover:text-[#B8945A] transition-colors">About</Link>
              <Link to="/cv" className="text-sm text-white/60 hover:text-[#B8945A] transition-colors">Curriculum Vitae</Link>
              <Link to="/contact" className="text-sm text-white/60 hover:text-[#B8945A] transition-colors">Contact</Link>
            </nav>
          </div>
          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] text-[#B8945A] mb-5 font-sans font-bold">Contact</h4>
            <div className="space-y-3">
              {personalInfo.contact.email && <a href={`mailto:${personalInfo.contact.email}`} className="flex items-center gap-3 text-sm text-white/60 hover:text-[#B8945A] transition-colors"><Mail size={14} className="text-[#B8945A] flex-shrink-0" /><span>{personalInfo.contact.email}</span></a>}
              {personalInfo.contact.emailWork && <a href={`mailto:${personalInfo.contact.emailWork}`} className="flex items-center gap-3 text-sm text-white/60 hover:text-[#B8945A] transition-colors"><Mail size={14} className="text-[#B8945A] flex-shrink-0" /><span>{personalInfo.contact.emailWork}</span></a>}
              {personalInfo.contact.phone && <div className="flex items-center gap-3 text-sm text-white/60"><Phone size={14} className="text-[#B8945A] flex-shrink-0" /><span>{personalInfo.contact.phone}</span></div>}
              {personalInfo.contact.phoneUK && <div className="flex items-center gap-3 text-sm text-white/60"><Phone size={14} className="text-[#B8945A] flex-shrink-0" /><span>{personalInfo.contact.phoneUK}</span></div>}
              {personalInfo.contact.address && <div className="flex items-center gap-3 text-sm text-white/60"><MapPin size={14} className="text-[#B8945A] flex-shrink-0" /><span>{personalInfo.contact.address}</span></div>}
              <SocialLinksRow links={socialLinks} legacyLinkedin={personalInfo.social.linkedin} color="text-white/60" hoverColor="hover:text-[#B8945A]" iconSize={14} className="mt-2" testid="footer-social-links" />
            </div>
          </div>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-6">
          <p className="text-center text-xs text-white/40 font-sans" data-testid="footer-copyright">
            &copy; 2000-{String(new Date().getFullYear()).slice(-2)} {personalInfo.name}. All rights reserved
            {" · "}<Link to="/privacy" className="hover:text-[#B8945A] transition-colors">Privacy Policy</Link>
            {" · "}<Link to="/terms" className="hover:text-[#B8945A] transition-colors">Terms</Link>
          </p>
        </div>
      </div>
    </footer>
  );
};

const Layout = ({ children }) => {
  const { favicon, isPreview } = useSiteContent();
  const faviconUrl = favicon?.url ? (favicon.url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${favicon.url}` : favicon.url) : null;

  // Update favicon dynamically using useEffect (React 19 compatible)
  useEffect(() => {
    if (faviconUrl) {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.getElementsByTagName('head')[0].appendChild(link);
      }
      link.href = faviconUrl;
    }
  }, [faviconUrl]);

  return (
    <>
      {isPreview && (
        <div className="fixed top-0 left-0 right-0 z-[100] bg-[#B8945A] text-white text-center py-2 font-sans text-sm tracking-wide" data-testid="preview-banner">
          Preview Mode — You are viewing unsaved draft changes
          <button onClick={() => window.close()} className="ml-4 underline text-xs opacity-80 hover:opacity-100">Close Preview</button>
        </div>
      )}
      <ScrollToTop />
      <Header />
      <main className={isPreview ? "pt-[112px]" : "pt-[72px]"}>{children}</main>
      <Footer />
      <CookieConsent />
      <Toaster />
    </>
  );
};

export default Layout;
